import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createSchool } from "@/services/systemAdminService";
import type { LicenseInventoryRow } from "@/types/systemAdmin";

// Two call sites: the top-level "Add organization" card, where the admin
// picks a district from every district on the page, and each district's own
// card, where the district is already known — passing districtId/districtName
// there fixes the school to that district and drops the selector entirely,
// rather than showing a selector whose only sensible answer is already chosen.
interface AddSchoolDialogProps {
  districtRows?: LicenseInventoryRow[];
  districtId?: string;
  districtName?: string;
  onCreated: () => Promise<void>;
}

export const AddSchoolDialog = ({
  districtRows,
  districtId: fixedDistrictId,
  districtName: fixedDistrictName,
  onCreated,
}: AddSchoolDialogProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [districtId, setDistrictId] = useState(fixedDistrictId ?? "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    setDistrictId(fixedDistrictId ?? "");
    setName("");
    setOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim() || !districtId) return;

    setBusy(true);
    try {
      await createSchool(districtId, name);
      toast({ title: "School created" });
      setOpen(false);
      await onCreated();
    } catch (err) {
      toast({
        title: "Could not create school",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={fixedDistrictId ? "outline" : "default"}
        size={fixedDistrictId ? "sm" : "default"}
        aria-label={fixedDistrictName ? `Add school to ${fixedDistrictName}` : undefined}
        onClick={openDialog}
      >
        <Plus className="h-4 w-4" aria-hidden />
        Add school
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add school</DialogTitle>
            <DialogDescription>
              {fixedDistrictName
                ? `Create a new school in ${fixedDistrictName}.`
                : "Create a new school within an existing district."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {fixedDistrictId ? null : (
              <div className="flex flex-col gap-2">
                <label htmlFor="add-school-district" className="text-sm text-gray-600">
                  District
                </label>
                <Select value={districtId} onValueChange={setDistrictId}>
                  <SelectTrigger id="add-school-district">
                    <SelectValue placeholder="Select district…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(districtRows ?? []).map((d) => (
                      <SelectItem key={d.districtId} value={d.districtId}>
                        {d.districtName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label htmlFor="add-school-name" className="text-sm text-gray-600">
                Name
              </label>
              <Input
                id="add-school-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="School name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={busy || !name.trim() || !districtId}
              onClick={() => void handleCreate()}
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Adding…
                </>
              ) : (
                "Add school"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
